import { X, Loader2 } from 'lucide-react';
import { useState } from 'react';
import ShareStoryCard from './ShareStoryCard';
import { ShareActions } from './ShareActions';
import { useShareCardGenerator } from '../hooks/useShareCardGenerator';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShareMenu = ({ story, onClose }) => {
  const { cardRef, previewUrl, blob, generating, error } = useShareCardGenerator(story);
  const [shareCount, setShareCount] = useState(story.share_count || 0);

  // Bot-readable route: real OG/og:image meta for crawlers, 302→SPA for humans
  const shareUrl = `${BACKEND_URL}/api/share/story/${story.id}`;
  const shareText = `${story.primary_headline}\n\nSee how every side is reporting this on Clarity News — ${story.article_count} sources, one spectrum.`;

  const handleDownload = () => {
    if (!previewUrl) return;
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `clarity-${story.id}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    handleShared();
  };

  const handleShared = () => {
    setShareCount((c) => c + 1);
    fetch(`${BACKEND_URL}/api/stories/${story.id}/share`, { method: 'POST' }).catch((e) =>
      logError('ShareMenu:incrementShareCount', e)
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="share-menu"
    >
      <div
        className="relative bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Share this story</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              A card of every side, ready to send
              {shareCount > 0 && <span data-testid="share-count-text"> · Shared {shareCount} {shareCount === 1 ? 'time' : 'times'}</span>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-gray-100 transition-colors"
            aria-label="Close share menu"
            data-testid="share-close-button"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Preview */}
        <div className="p-4 sm:p-5 bg-gray-50">
          <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-900 aspect-[1200/630] flex items-center justify-center">
            {generating && (
              <div className="flex flex-col items-center text-gray-300 gap-2" data-testid="share-preview-loading">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span className="text-xs">Crafting your card…</span>
              </div>
            )}
            {!generating && error && (
              <div className="text-center text-sm text-red-300 px-4">{error}</div>
            )}
            {!generating && previewUrl && (
              <img
                src={previewUrl}
                alt="Story share preview"
                className="w-full h-full object-cover"
                data-testid="share-preview-image"
              />
            )}
          </div>
        </div>

        <ShareActions
          story={story}
          shareUrl={shareUrl}
          shareText={shareText}
          previewUrl={previewUrl}
          blob={blob}
          generating={generating}
          onDownload={handleDownload}
          onShared={handleShared}
        />
      </div>

      {/* Hidden off-screen render target */}
      <div style={{ position: 'fixed', left: '-99999px', top: 0, pointerEvents: 'none' }} aria-hidden="true">
        <ShareStoryCard story={story} ref={cardRef} />
      </div>
    </div>
  );
};

export default ShareMenu;
